import { Transform, Expose, Exclude, Type } from 'class-transformer';
import type { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, anyToDate } from './Models';

@Exclude()
export class Document extends _FirebaseDocument {
    @Expose()
    public status: DocumentStatus = DocumentStatus.UNKNOWN
    @Expose()
    public filename: string
    // The source-side name: ingestion renames a converted file (`Plan.docx` to
    // `Plan.docx.pdf`) and keeps the name it had at the source here.
    @Expose()
    public original_filename?: string
    @Expose()
    public mime_type?: string
    @Expose()
    public sha256: string
    @Expose()
    public size: number
    @Expose()
    public storage_path: string
    @Expose()
    public filepath?: string
    @Expose()
    @Transform(anyToDate)
    public uploaded_at: Date
    @Expose()
    public uploader: string
    @Expose()
    public hash?: string
    @Expose()
    public hash_method?: string
    @Expose()
    public failed_count?: number
    @Expose()
    public failed?: boolean
    @Expose()
    public indexed?: boolean
    @Expose()
    @Transform(anyToDate)
    public last_indexed?: Date
    @Expose()
    public loading_batch_id?: string
    @Expose()
    public source_type?: DocumentSourceType | (string & {})
    @Expose()
    public source_id?: string

    // Source metadata
    @Expose()
    @Type(() => DocumentGoogleDriveMetadata)
    public gdrive_metadata?: DocumentGoogleDriveMetadata
    @Expose()
    @Type(() => DocumentO365Metadata)
    public o365_metadata?: DocumentO365Metadata
    @Expose()
    @Type(() => DocumentManualUploadMetadata)
    public manual_upload_metadata?: DocumentManualUploadMetadata
    @Expose()
    @Type(() => DocumentAtlassianMetadata)
    public atlassian_metadata?: DocumentAtlassianMetadata
    @Expose()
    @Type(() => DocumentOdooMetadata)
    public odoo_metadata?: DocumentOdooMetadata
    @Expose()
    @Type(() => DocumentQualishareMetadata)
    public qualishare_metadata?: DocumentQualishareMetadata
    @Expose()
    @Type(() => DocumentTracexMetadata)
    public tracex_metadata?: DocumentTracexMetadata

    collection(): string {
        return "documents";
    }
    // removed date formatting getter; use vue-i18n $d in components instead

    static fromFirebase(o: DocumentSnapshot): Document | null {
        return _FirebaseDocument._fromFirebase(Document, o);
    }

    // Helper to resolve the collection path depending on scope (project-level or datasource-level)
    static collectionPath(projectId: string, datasourceId?: string): string {
        if (!projectId) return ''
        return datasourceId
            ? `projects/${projectId}/datasources/${datasourceId}/documents`
            : `projects/${projectId}/documents`
    }
}

/**
 * The name the client knows the document by (MYAC-1690): a converted file keeps
 * the name it had at the source, `filename` is the converted copy's.
 */
export function documentDisplayName(doc: Pick<Document, 'filename' | 'original_filename'>): string {
    return doc.original_filename || doc.filename
}

/**
 * The sources the sync services write today. The field stays open to any
 * string: a connector added on the backend must not break this build's reads.
 */
export type DocumentSourceType = 'gdrive' | 'o365' | 'tracex' | 'atlassian' | 'odoo' | 'qualishare' | 'upload'

export enum DocumentStatus {
    TO_CONVERT = 'to_convert',
    TO_LOAD = 'to_load',
    // MYAC-1651 — downloaded and converted, resting until it is read (a readable_first datasource).
    READABLE = 'readable',
    TO_STRUCTURE = 'to_structure',
    TO_COMPARE = 'to_compare',
    TO_INDEX = 'to_index',
    INDEXING = 'indexing',
    TO_DELETE = 'to_delete',
    DONE = 'done',
    FAILED = 'failed',
    UNKNOWN = 'unknown',
}

export class DocumentGoogleDriveFolder {
    @Expose()
    public id: string
    @Expose()
    public name: string
    @Expose()
    public parents?: string[]
    @Expose()
    public mimeType: string
    @Expose()
    public driveId?: string
}

export class DocumentGoogleDriveParentFolders {
    @Expose()
    @Type(() => DocumentGoogleDriveFolder)
    public folders: DocumentGoogleDriveFolder[] = []
}

export class DocumentGoogleDriveMetadata {
    @Expose()
    public file_id?: string
    @Expose()
    public filename?: string
    @Expose()
    public mime_type?: string
    @Expose()
    public modified_time?: string
    @Expose()
    public md5_checksum?: string
    @Expose()
    public drive_id?: string
    @Expose()
    @Type(() => DocumentGoogleDriveParentFolders)
    public parents?: DocumentGoogleDriveParentFolders
    @Expose()
    public owner_email?: string
    @Expose()
    public version?: string
}

export class DocumentO365Metadata {
    @Expose()
    public file_id?: string
    @Expose()
    public filename?: string
    @Expose()
    public site_id?: string
    @Expose()
    public drive_id?: string
    @Expose()
    public parent_folder_id: string | null = null
    @Expose()
    public sync_root_folder_ids: string[] = []
    @Expose()
    public mime_type?: string
    @Expose()
    public size?: number
    @Expose()
    @Transform(anyToDate)
    public last_modified_date_time?: Date
    @Expose()
    public crc32_hash?: string
    @Expose()
    public sha1_hash?: string
    @Expose()
    public sha256_hash?: string
}

export class DocumentManualUploadMetadata {
    @Expose()
    public filename?: string
    @Expose()
    public uploader?: string
    @Expose()
    @Transform(anyToDate)
    public uploaded_at?: Date
}

export class DocumentAtlassianMetadata {
    /** 'jira_issue' | 'jira_attachment' | 'confluence_page' | 'confluence_attachment', and more to come. */
    @Expose()
    public source_module?: string
    @Expose()
    public atlassian_id?: string
    @Expose()
    public issue_key?: string
    @Expose()
    public space_key?: string
    /** The issue or page an attachment hangs on. */
    @Expose()
    public parent_id?: string
    @Expose()
    public web_url?: string
}

export class DocumentOdooMetadata {
    @Expose()
    public odoo_model?: string
    @Expose()
    public odoo_id?: number
    @Expose()
    public res_model?: string
    @Expose()
    public res_id?: number
    @Expose()
    public url?: string
}

export class DocumentQualishareMetadata {
    @Expose()
    public portal?: string
    /** The SharePoint site segment, written with its leading slash (`/Qualite`). */
    @Expose()
    public site?: string
    @Expose()
    public sp_list_guid?: string
    @Expose()
    public sp_item_id?: number
    @Expose()
    public export_type?: string
}

export class DocumentTracexMetadata {
    @Expose()
    public source_module?: string
    @Expose()
    public huly_id?: string
}
